import React from "react";
import { StatusIndicator } from "./StatusIndicator";

/** Shown while the Business Profile card is being built: from the moment the
 *  agent asks for it (still streaming) until the audit lands. Same shell and
 *  rough height as the finished card, so it is replaced in place, not pushed. */
export const GBP_LOADING_TEXT = "Fetching your Google Business Profile data…";

/** A soft sweep instead of flat grey blocks; still in reduced motion. */
const BAR =
  "rounded-full bg-[linear-gradient(90deg,rgba(148,163,184,0.14)_25%,rgba(148,163,184,0.28)_50%,rgba(148,163,184,0.14)_75%)] bg-[length:200%_100%] motion-safe:animate-shimmer";

interface GbpLoadingCardProps {
  /** Hide the "Fetching…" line when the agent's reply above already says it
   *  (a typed request). It stays for screen readers. */
  quiet?: boolean;
  /** SGL-421 plan card (the starter button): the same "Working on it…" every
   *  agent shows, in place of the one "Fetching…" line. */
  working?: boolean;
}

export const GbpLoadingCard: React.FC<GbpLoadingCardProps> = ({ quiet = false, working = false }) => (
  <div role="status" aria-live="polite" className="w-full space-y-4 text-left">
    {quiet ? (
      <span className="sr-only">{GBP_LOADING_TEXT}</span>
    ) : working ? (
      <StatusIndicator status="thinking" />
    ) : (
      <p className="inline-flex items-center gap-2.5 text-[14px] font-medium">
        <span
          aria-hidden="true"
          className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-theme-border border-t-theme-fg motion-safe:animate-spin"
        />
        <span className="bg-[linear-gradient(90deg,var(--color-text-secondary)_0%,var(--color-text-primary)_50%,var(--color-text-secondary)_100%)] bg-[length:200%_100%] bg-clip-text text-transparent motion-safe:animate-shimmer">
          {GBP_LOADING_TEXT}
        </span>
      </p>
    )}
    {/* The finished card's outline: header, Approve all strip, one change with
        its bottom bar. The card then replaces it in place. */}
    <div
      aria-hidden="true"
      className="overflow-hidden rounded-[14px] border border-theme-border bg-theme-surface shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-18px_rgba(16,24,40,0.22)]"
    >
      <div className="grid gap-2.5 px-5 pb-4 pt-5 sm:px-6">
        {quiet ? (
          <p className="inline-flex items-center gap-2 text-[12.5px] text-theme-fg-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-primary motion-safe:animate-pulse" />
            Opening your proposal
          </p>
        ) : (
          <div className={`h-2.5 w-28 ${BAR}`} />
        )}
        <div className={`h-[18px] w-1/2 ${BAR}`} />
        <div className={`h-2.5 w-3/4 ${BAR}`} />
      </div>
      <div className="flex items-center gap-3 border-y border-theme-border bg-theme-background px-5 py-3 sm:px-6">
        <div className={`h-8 w-28 ${BAR}`} />
        <div className={`h-2.5 w-48 ${BAR}`} />
      </div>
      <div className="grid gap-3 px-5 py-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div className={`h-2.5 w-14 ${BAR}`} />
          <div className="flex gap-1">
            <span className="h-1.5 w-4 rounded-full bg-theme-border" />
            {[0, 1, 2].map((i) => (
              <span key={i} className="h-1.5 w-1.5 rounded-full bg-theme-border" />
            ))}
          </div>
        </div>
        <div className={`h-4 w-1/3 ${BAR}`} />
        <div className={`h-2.5 w-full ${BAR}`} />
        <div className={`h-2.5 w-2/3 ${BAR}`} />
        <div className="mt-1 flex items-center gap-2">
          <div className={`h-8 w-24 ${BAR}`} />
          <div className={`h-8 w-16 ${BAR}`} />
          <div className="ml-auto flex gap-3">
            <div className={`h-2.5 w-10 ${BAR}`} />
            <div className={`h-2.5 w-10 ${BAR}`} />
          </div>
        </div>
      </div>
    </div>
  </div>
);
